import { Fragment, MouseEvent, useEffect, useState } from 'react';

import Menu from '~/src/common/components/Menu';
import { type Navigation } from '~/src/common/components/Navigation/types';
import { useFeatureFlag } from '~/src/common/services/FeatureFlag';
import { useNiceModal } from '~/src/common/services/ModalsManager';
import { ImageParams } from '~/src/common/typings/image';
import { getImageUrl, prefetchImage } from '~/src/common/utils/cloudinary';

import { getCategoryPageLink, getSubcategoryAnchorLink } from '../utils';

import styles from './Menu.module.scss';

interface Props {
  navigation: Navigation;
  isActiveID: (id: string) => boolean;
  onLinkClick: (id: string) => void;
}

const CategoryMenuClassic = ({ navigation, isActiveID, onLinkClick }: Props) => {
  const isBoosterEpicerieEnabled = useFeatureFlag('booster-epicerie');
  const categoriesModal = useNiceModal('categories-modal');
  const subCategoriesModal = useNiceModal('sub-categories-modal');
  // La famille et la catégorie en cours
  const family = navigation.families.find(item => isActiveID(item.id));
  const category = family?.categories.find(item => isActiveID(item.id));

  // La liste des catégories -- toutes familles confondues
  const categories = navigation.families.flatMap(fam => fam.categories);

  // Détecte si une catégorie appartient à la famille active
  const familyCategoryIDs = new Set(family?.categories.map(c => c.id));
  const isFromActiveFamily = (id: string) => familyCategoryIDs.has(id);

  // ID de l'élément ouvert et réouverture au changement
  const [expandedID, setExpandedID] = useState(category?.id);
  useEffect(() => setExpandedID(category?.id), [category?.id]);
  const handleCategoryClick = (item: { id: string }, event: MouseEvent) => {
    setExpandedID(item.id === expandedID ? undefined : item.id);
    if (item.id === category?.id) event.preventDefault();
    onLinkClick(item.id); // Tracking sur les catégories
  };

  // Tracking sur les sous-catégories
  const handleSubcategoryClick = (item: { id: string }) => {
    onLinkClick(item.id);
  };

  const openCategoryModal = () => {
    void categoriesModal.show({ categoriesByFamily: navigation.families });
  };

  const openSubcategoryModal = () => {
    if (!category) return;

    void subCategoriesModal.show({ category });
  };

  // Préchargement des images des sous-catégories pour éviter d'avoir de la latence sur l'affichage des images
  useEffect(() => {
    if (!isBoosterEpicerieEnabled) return;
    // Timeout pour laisser le temps de charger le reste car non prioritaire
    setTimeout(() => {
      for (const cat of categories)
        for (const sub of cat.children)
          prefetchImage(getImageUrl(sub.image, ImageParams.subcategoryTreeView));
    }, 500);
  }, [isBoosterEpicerieEnabled, categories]);

  return (
    <>
      <Menu mobile="primary" desktop="primary" className={styles.menu}>
        <Menu.Title visibility="desktop">{family?.name}</Menu.Title>
        {isBoosterEpicerieEnabled ? (
          <Menu.Icon
            name="list-normal"
            color="NEUTRAL_WHITE"
            onClick={openCategoryModal}
            size={24}
          />
        ) : null}
        {categories.map(cat => (
          <Fragment key={cat.id}>
            <Menu.Entry
              key={cat.id}
              href={getCategoryPageLink(cat)}
              isActive={isActiveID(cat.id)}
              isExpanded={cat.id === expandedID}
              onClick={event => handleCategoryClick(cat, event)}
              visibility={isFromActiveFamily(cat.id) ? 'all' : 'mobile'}>
              {cat.name}
            </Menu.Entry>
            {cat.id === category?.id && (
              <Menu visibility="desktop" mobile="secondary" desktop="secondary">
                {category.children.map(sub => (
                  <Menu.Entry
                    key={sub.id}
                    isActive={isActiveID(sub.id)}
                    onClick={() => handleSubcategoryClick(sub)}
                    href={getSubcategoryAnchorLink(category, sub)}>
                    {isBoosterEpicerieEnabled ? (
                      <img
                        src={getImageUrl(sub.image, ImageParams.subcategoryTreeView)}
                        alt={sub.name}
                      />
                    ) : null}
                    {sub.name}
                  </Menu.Entry>
                ))}
              </Menu>
            )}
          </Fragment>
        ))}
      </Menu>
      <Menu mobile="secondary" desktop="secondary" visibility="mobile">
        {isBoosterEpicerieEnabled ? (
          <Menu.Icon
            name="list-normal"
            color="ACTION_DEFAULT"
            onClick={openSubcategoryModal}
            size={24}
          />
        ) : null}
        {category?.children.map(sub => (
          <Menu.Entry
            key={sub.id}
            isActive={isActiveID(sub.id)}
            href={getSubcategoryAnchorLink(category, sub)}>
            {sub.name}
          </Menu.Entry>
        ))}
      </Menu>
    </>
  );
};

export default CategoryMenuClassic;
